import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { usePreferencesForm, PreferencesFields } from "/imports/ui/components/preferences";
import api from "/imports/api/client";

function PreferencesView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { slug } = useWidget();

  const { preferencesToken, afterSave = "home", redirect = "", email = "" } = location.state || {};

  const form = usePreferencesForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Pre-checked, mirroring the signup form (default-in + easy opt-out strategy)
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);

  // Unverified signups land on a dedicated "check your email" page; already-verified
  // ones (onboard / email-claim) go straight into the app.
  const leave = () => {
    if (afterSave === "login") {
      navigate(`/${slug}/check-email`, { replace: true, state: { email, redirect } });
    } else {
      navigate(`/${slug}/${redirect}`, { replace: true });
    }
  };

  // The token from signup is the only thing authorizing the save. Reaching this
  // page without one (e.g. a manual visit) means there's nothing to collect.
  useEffect(() => {
    if (!preferencesToken) navigate(`/${slug}/`, { replace: true });
  }, [preferencesToken, navigate, slug]);

  if (!preferencesToken) {
    return null;
  }

  const finish = ({ skipped }) => {
    if (afterSave !== "login" && !skipped) {
      enqueueSnackbar("Preferences saved.", { variant: "success" });
    }
    leave();
  };

  const handleSkip = async () => {
    setSubmitting(true);
    try {
      await api.savePreferences({ preferencesToken, skip: true });
    } catch (err) {
      // Skipping should never block the signup flow.
    } finally {
      setSubmitting(false);
      finish({ skipped: true });
    }
  };

  const handleSave = async () => {
    const { payload, error: validationError } = form.buildPayload();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      // Unchecked sends nothing — consent stays "unknown" rather than opted out
      await api.savePreferences({ preferencesToken, ...payload, ...(whatsappOptIn ? { whatsappOptIn: true } : {}) });
      finish({ skipped: false });
    } catch (err) {
      setError(err.message || "We couldn't save your preferences. You can set them later from your account.");
      setSubmitting(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh" py={4}>
      <Card sx={{ maxWidth: "35rem", width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
          <Typography variant="h5" fontWeight={600} textAlign="center" gutterBottom>
            Tell us your preferences
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" mb={3}>
            This helps your club tailor recommendations to you. You can skip and update these anytime.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <PreferencesFields form={form} />

          <FormControlLabel
            sx={{ mt: 2, alignItems: "flex-start", ".MuiCheckbox-root": { pt: 0 } }}
            control={<Checkbox checked={whatsappOptIn} onChange={(e) => setWhatsappOptIn(e.target.checked)} />}
            label={
              <Typography variant="body2" color="textSecondary">
                Send me club offers &amp; updates (you can opt out anytime)
              </Typography>
            }
          />

          <Button variant="contained" fullWidth size="large" disabled={submitting} sx={{ mt: 3 }} onClick={handleSave}>
            {submitting ? "Saving..." : "Save preferences"}
          </Button>
          <Button fullWidth disabled={submitting} sx={{ mt: 1 }} onClick={handleSkip}>
            Skip for now
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

export default PreferencesView;
